import trackEvent from "components/tracking/trackEvent";
import useAppsflyerOneLink, { forQrCode, getFallbackOnelinkUrl, useQrCodeCanvas, withAdSet, withCurrentPath } from "components/common/useAppsflyerOneLink";
import Icon from "components/Icons/Icon";
import trackFathom from "components/tracking/trackFathom";
import { t } from "i18n/index";
import { useMarketingLang } from "i18n/useMarketingLang";
import TravelJournalPreview from "img/travel-journal-preview.png";
import TripRoutePreview from "img/trip-route-1.webp";
import { useEffect, useRef } from "react";
import apple from "../img/apple-appstore.svg";
import google from "../img/playstore.svg";
import qrCode from "../img/qr-download-app.png";
import StipplLogoImpact from "../img/stippl-logo--impact.svg";
import { getChromeTranslations } from "../marketing/i18n/pages/chrome";
import "./DownloadAppModal.scss";

// Same "S" glyph path used across the product pages (e.g. hero watermarks, DownloadAppQrCard).
const S_PATH =
	"M17.3912 99.1864C14.9217 99.1864 12.9613 101.159 13.0092 103.593C13.0589 106.025 15.0991 108 17.5668 108C20.0363 108 21.9967 106.025 21.9488 103.593C21.8991 101.159 19.8589 99.1864 17.3912 99.1864ZM65.3336 33.3614C76.1235 30.4927 80.4221 24.1043 80.431 16.7419C80.4434 6.75566 72.3519 0 59.0871 0C38.1939 0 0 22.4846 0 43.1187C0 59.3036 21.0387 64.5548 35.3004 67.8972C49.5639 71.2377 60.588 74.2732 60.588 78.2187C60.588 82.1625 48.0081 91.0523 27.7464 97.4815C25.575 98.2036 24.3065 100.144 24.9097 101.816C25.5147 103.487 27.7145 104.079 29.9356 103.533C38.3749 101.452 78.201 92.3474 78.201 74.4204C78.201 56.4935 35.2862 49.1612 35.2862 45.9005C35.2862 42.5528 56.6017 35.6819 65.3336 33.3614Z";

const STAR_PATH = "M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z";

// AppsFlyer's aggregated reporting API groups by Adset but not by sub params beyond af_sub1
// (already spent on the page path) — see withAdSet's own doc comment for the full reasoning.
// Tags this header modal apart from the floating card (AF_ADSET in DownloadAppQrCard.tsx) and the
// in-flow inline block (InlineInstallCard.tsx), which otherwise ride the same OneLink and the
// same c=web_blog/web_discover campaign.
const AF_ADSET = "download_modal";

interface DownloadAppModalProps {
	isOpen: boolean;
	onClose: () => void;
	location: string;
}

const DownloadAppModal = ({ isOpen, onClose, location }: DownloadAppModalProps) => {
	const lang = useMarketingLang();
	const translations = getChromeTranslations(lang);
	const oneLinkUrl = useAppsflyerOneLink();
	const qrCanvasRef = useRef<HTMLCanvasElement>(null);
	const currentPath = typeof window === "undefined" ? "" : window.location.pathname;
	// oneLinkUrl is cached from whichever page the Smart Script first ran on — rewrite af_sub1 so it
	// still reflects the page the modal is actually open on, without re-triggering AppsFlyer's SDK
	// (which would risk losing the first-touch mediaSource/campaign values). Falls back to
	// getFallbackOnelinkUrl() (still OS-smart-redirects, just without attribution) if OneLink never
	// resolves (e.g. Smart Script blocked by an ad blocker). withAdSet applies to both branches — the
	// fallback link is exactly as separable from the other install surfaces as the live one.
	const trackedUrl = withAdSet(oneLinkUrl ? withCurrentPath(oneLinkUrl, currentPath) : getFallbackOnelinkUrl(), AF_ADSET);
	// Gated on isOpen — the canvas below only exists once the modal is open, so drawing must wait
	// until then. forQrCode strips the Smart Script's own bookkeeping params and marks the scan with
	// af_channel=qr — taps on the hrefs below keep trackedUrl as-is, only the rendered QR payload
	// goes through it.
	const qrRendered = useQrCodeCanvas(qrCanvasRef, isOpen, forQrCode(trackedUrl), {
		colorDark: "#1b3d49",
	});

	useEffect(() => {
		if (!isOpen) return undefined;

		document.body.style.overflow = "hidden";
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === "Escape") onClose();
		};
		window.addEventListener("keydown", handleKeyDown);

		return () => {
			document.body.style.overflow = "";
			window.removeEventListener("keydown", handleKeyDown);
		};
	}, [isOpen, onClose]);

	if (!isOpen) return null;

	const handleTracking = (type: "apple" | "android") => {
		trackEvent(type === "android" ? "click_googleplay_cta" : "click_appstore_cta", { placement: "download_modal" }, "marketing_site");
		trackFathom(type === "android" ? "BNQKH0MH" : "FD21GVOP");
	};

	return (
		<div className="download-app-modal-overlay" onMouseDown={onClose}>
			<div className="download-app-modal" onMouseDown={(e) => e.stopPropagation()}>
				<button type="button" className="download-app-modal__close" aria-label={t(translations, "downloadModal.close", "Close")} onClick={onClose}>
					<Icon name="close" size={12} fill="currentColor" />
				</button>

				<div className="download-app-modal__hero">
					<svg className="download-app-modal__hero-mark" width="260" height="346" viewBox="0 0 81 108" fill="none">
						<path d={S_PATH} fill="#fff" />
					</svg>
					<img className="download-app-modal__logo" src={StipplLogoImpact} width={104} height={28} alt="Stippl" />
					<div className="download-app-modal__title">
						{t(translations, "downloadModal.titlePrefix", "Plan ")}
						<span className="download-app-modal__title-accent">{t(translations, "downloadModal.titleAccent", "smarter")}</span>,<br />
						{t(translations, "downloadModal.titleSuffix", "travel better")}
					</div>
					<p className="download-app-modal__subtitle">{t(translations, "downloadModal.subtitle", "Join 2M+ travelers planning trips worth remembering.")}</p>
					<img
						className="download-app-modal__travel-journal-image"
						src={TravelJournalPreview}
						alt={t(translations, "downloadModal.journalImageAlt", "travel journal")}
						loading="lazy"
					/>
					<img
						className="download-app-modal__trip-route-image"
						src={TripRoutePreview}
						alt={t(translations, "downloadModal.routeImageAlt", "trip route")}
						loading="lazy"
					/>
				</div>

				<div className="download-app-modal__footer">
					<div className="download-app-modal__rating">
						<div className="download-app-modal__stars">
							{[0, 1, 2, 3, 4].map((i) => (
								<svg key={i} width="14" height="14" viewBox="0 0 24 24" fill="#ffc14a">
									<path d={STAR_PATH} />
								</svg>
							))}
						</div>
						<span className="download-app-modal__rating-score">4.7/5</span>
						<span className="download-app-modal__rating-count">{t(translations, "downloadModal.ratingCount", "(10k+ ratings)")}</span>
					</div>

					<div className="download-app-modal__actions">
						<div className="download-app-modal__badges">
							{/* stippl.onelink.me itself isn't blocked by ad blockers (only the Smart Script that
							generates the attributed URL is) — so this still smart-redirects and registers a
							click with AppsFlyer, just without the richer incoming-URL attribution mapping. */}
							<a
								href={trackedUrl}
								aria-label={t(translations, "downloadModal.appStoreLabel", "Download Stippl on the App Store")}
								target="_blank"
								rel="noreferrer"
								onClick={() => handleTracking("apple")}
							>
								<img src={apple} width={140} height={41} alt={t(translations, "downloadModal.appStoreAlt", "App Store")} />
							</a>
							<a
								href={trackedUrl}
								aria-label={t(translations, "downloadModal.googlePlayLabel", "Get Stippl on Google Play")}
								target="_blank"
								rel="noreferrer"
								onClick={() => handleTracking("android")}
							>
								<img src={google} width={140} height={41} alt={t(translations, "downloadModal.googlePlayAlt", "Google Play")} />
							</a>
						</div>
						<div className="download-app-modal__qr">
							<span className="download-app-modal__qr-label">
								{/* One line. The <br/> that used to split these produced the staggered label in
								    123yb691581: the box shrinks to its widest line, so "Scan to" centred itself
								    over "download" and the two read as broken text. Both keys are kept so the
								    seven locales still apply; they are joined rather than replaced. */}
								{t(translations, "downloadModal.qrLabelLine1", "Scan to")} {t(translations, "downloadModal.qrLabelLine2", "download")}
							</span>
							<svg className="download-app-modal__qr-card__arrow" width="46" height="52" viewBox="0 0 46 52" fill="none">
								<path d="M38 4C45 24 40 42 12 44" stroke="#13c892" stroke-width="2.4" stroke-linecap="round" fill="none"></path>
								<path d="M23 38L11 44L21 50" stroke="#13c892" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" fill="none"></path>
							</svg>
							<canvas
								ref={qrCanvasRef}
								className="download-app-modal__qr-canvas"
								width={132}
								height={132}
								style={{ display: qrRendered ? "block" : "none" }}
							/>
							{!qrRendered && (
								<img src={qrCode} width={132} height={132} alt={t(translations, "downloadModal.qrAlt", "Scan to download the Stippl app")} />
							)}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default DownloadAppModal;
